import { Text, Card, Box, InlineGrid, Button } from "@shopify/polaris";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  setModalxComponent,
  setModalxComponentProps,
} from "components/modalx/modalx.slice";
import ProjectColumnSelect from "components/project-column-select/project-column-select";
import UserSelectByPermissions from "features/user-select-by-permission/user-select-by-permission";
import PrettyDate from "components/format/PrettyDate";
import ProjectContactSummary from "components/project/project-contact-summary/project-contact-summary";

function formatEur(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return null;
  }
  return new Intl.NumberFormat("lt-LT", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(Number(value));
}

function ProjectDetails({
  id,
  projectBoardId,
  name,
  description,
  projectBoardColumnEntity,
  userEntity,
  updateHandler,
  dueDate,
  reporterEntity,
  createdAt,
  valueEur,
  contactName,
  contactEmail,
  contactPhone,
  contactLinkedin,
  contactWhatsapp,
  contactViber,
  /** When true, stage is changed via the journey bar instead of a dropdown. */
  hideColumnSelect = false,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const projectName = name ? (
    <Text as="span">
      {t("title")}
      <Text as="span" variant="bodyMd">
        : {name}
      </Text>
    </Text>
  ) : (
    <Text as="p" tone="subdued">
      {t("no_name_provided")}
    </Text>
  );
  const projectDescription = description ? (
    <Text as="span">
      {t("description")}
      <Text as="span" variant="bodyMd">
        : {description}
      </Text>
    </Text>
  ) : (
    <Text as="p" tone="subdued">
      {t("no_description_provided")}
    </Text>
  );

  // project due Date Node
  const dueDateNode = dueDate ? (
    <Text as="p">
      {t("due_date")}: <PrettyDate dateString={dueDate} />
    </Text>
  ) : null;

  const createdByNode = reporterEntity ? (
    <Text as="p">
      {t("created_by")}: {reporterEntity.name}
    </Text>
  ) : null;

  const createdAtNode = createdAt ? (
    <Text as="p">
      {t("createdAt")}: <PrettyDate dateString={createdAt} />
    </Text>
  ) : null;

  const valueLabel = formatEur(valueEur);
  const valueNode = (
    <Text as="p">
      {t("project_value_eur", { defaultValue: "Value (EUR)" })}:{" "}
      {valueLabel || t("not_set", { defaultValue: "Not set" })}
    </Text>
  );

  const handleEditProjectClick = () => {
    dispatch(setModalxComponent("ProjectForm"));
    dispatch(setModalxComponentProps({ id: id, projectBoardId }));
  };

  const projectColumnHandler = (val) => {
    updateHandler(val, "project_column");
  };

  const userHandler = (val) => {
    updateHandler(val, "user");
  };

  return (
    <Card roundedAbove="sm">
      <InlineGrid columns="1fr auto">
        <Text as="h2" variant="headingSm">
          {t("project_summary", { defaultValue: "Summary" })}
        </Text>
        <Button
          variant="plain"
          onClick={handleEditProjectClick}
          accessibilityLabel="Preview"
        >
          {t("edit")}
        </Button>
      </InlineGrid>
      {!hideColumnSelect && projectBoardColumnEntity?.id && (
        <Box paddingBlockStart="200" paddingBlockEnd="200">
          <ProjectColumnSelect
            value={projectBoardColumnEntity.id}
            onChange={projectColumnHandler}
            projectBoardId={projectBoardId}
            isRequired={true}
          />
        </Box>
      )}
      <Box paddingBlockStart="200" paddingBlockEnd="200">
        <UserSelectByPermissions
          value={userEntity?.id}
          onChange={userHandler}
          label={"select_user"}
          Permission={"Access project"}
        />
      </Box>
      <InlineGrid>{projectName}</InlineGrid>
      <InlineGrid>{valueNode}</InlineGrid>
      <Box paddingBlockStart="300" paddingBlockEnd="200">
        <ProjectContactSummary
          contactName={contactName}
          contactEmail={contactEmail}
          contactPhone={contactPhone}
          contactLinkedin={contactLinkedin}
          contactWhatsapp={contactWhatsapp}
          contactViber={contactViber}
        />
      </Box>
      <InlineGrid>{createdByNode}</InlineGrid>
      <InlineGrid>{createdAtNode}</InlineGrid>
      <InlineGrid>{dueDateNode}</InlineGrid>
      <InlineGrid>{projectDescription}</InlineGrid>
    </Card>
  );
}

export default ProjectDetails;
